import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, pluck } from 'rxjs/operators';

import { IAirBookingResponse, IQualityCheckResponse } from '../model/booking/air/IAirBookingResponse';
import { ApiResourceService } from '../../apiResource/apiResource.service';
import { IAirCreateBookingRequest, IAirUpdateBookingRequest } from '../model/booking/air/IAirCreateBooking';
import { IGetBookingsAirResponse } from '../booking/interfaces/IGetBookings';
import { IGetEnumsService } from 'libraries/service/booking/enums/IGetEnums';
import { IAirBooking } from '../model/booking/air/IAirBooking';
import { EHosts } from "libraries/enums/EHosts";
import { IWenji } from "../../../interfaces/IWenji";
import { IHeader } from "../../resource/IHeader";
import { ResponseForConvertListOperationAfx2jsonType } from "@afidium/ts-facade";

declare const wenji: IWenji;

const AIR: string = "air";
const BOOKINGS: string = "bookings";
const BOOKING: string = "booking";
const CHECK: string = "check";
const HOSTS: string = "hosts";
const CANCEL: string = "cancel";
const CREATE: string = "create";

export const HOST_SERVICE_SELECTION_ALLOWED: EHosts[] = [
    EHosts.TRANSAVIA,
    EHosts.TRAVELFUSION,
    EHosts.TRAVELPORT,
    EHosts.GALILEO,
];

@Injectable()
export class AirBookingResourceService {

    constructor(
        private readonly apiResourceService: ApiResourceService,
    ) {
    }

    public get(host: string, id: string, target: string, cache: boolean = true, owner: string = null, format: string = "shared"): Observable<IGetBookingsAirResponse> {

        // simulate a booking that can not be retrieved
        // throw new Error('juste un test');

        return this.apiResourceService.getOne<IGetBookingsAirResponse>(
            this.apiResourceService.getApiUrl(AIR, BOOKINGS, id),
            {
                host,
                owner: owner ? owner : host,
                cache: cache ? "true" : "false",
                format
            },
            {
                target
            }
        );
    }

    public getWithServiceSelection(host: string, id: string, target: string, cache: boolean = false): Observable<any> {

        //only launch RQ if GALILEO / TRAVELPORT
        // if(host === EHosts.GALILEO || host === EHosts.TRAVELPORT){
        if (HOST_SERVICE_SELECTION_ALLOWED.includes(host as EHosts)) {
            let computedHost: string;
            let computedOwner: string;
            // #33068 only substitute owner and host in URL params for Galileo & Tport, keep as is for others
            if (host === EHosts.TRAVELPORT || host === EHosts.GALILEO) {
                computedHost = EHosts.TRAVELPORT;
                computedOwner = EHosts.GALILEO;
            } else {
                computedHost = host;
                computedOwner = host;
            }
            return this.apiResourceService.getOne<any>(
                this.apiResourceService.getApiUrl(AIR, BOOKINGS, id),
                {
                    host: computedHost,
                    owner: computedOwner,
                    cache,
                    format: "xsl_direct_convert_xft2json"
                },
                {
                    target
                }
            );
        }
        return of(null);

    }

    public getCachedOrFallback(host: string, id: string, target: string): Observable<IGetBookingsAirResponse> {
        return this.get(host, id, target, true)
            .pipe(
                catchError((error: Error) => {
                    console.warn("Error getting the booking in the cache");
                    return this.get(host, id, target, false);
                }),
            );
    }

    public cancel({host, syscode}: { host: string, syscode: string }, target: string): Observable<IAirBookingResponse> {
        return this.apiResourceService.postOne<void, IAirBookingResponse>(
            this.apiResourceService.getApiUrl(AIR, HOSTS, host, BOOKINGS, syscode, CANCEL),
            undefined,
            {
                target
            }
        );
    }

    public create(body: IAirCreateBookingRequest, target: string): Observable<IGetBookingsAirResponse> {
        return this.apiResourceService.postOne<IAirCreateBookingRequest, IGetBookingsAirResponse>(
            this.apiResourceService.getApiUrl(AIR, BOOKINGS, CREATE),
            body,
            {
                format: "shared"
            },
            {
                target,
            }
        );
    }

    public update(syscode: string, body: IAirUpdateBookingRequest): Observable<IGetBookingsAirResponse> {
        const headers: IHeader = {
            target: wenji.ui.target
        };
        return this.apiResourceService.putOne<IAirUpdateBookingRequest, IGetBookingsAirResponse | any>(
            this.apiResourceService.getApiUrl(BOOKING, BOOKINGS, syscode, 'sectors'),
            body,
            null,
            headers
        );
    }

    public getServices(body?: string[]): Observable<IGetEnumsService[]> {
        return this.apiResourceService.post<string[], IGetEnumsService>(
            this.apiResourceService.getApiUrl(AIR, 'enums', 'service', 'search'),
            body
        );
    }

    public qualityCheck(syscode: string, bookingHost: string, bookingCode: string, target?: string): Observable<ResponseForConvertListOperationAfx2jsonType> {
        return this.apiResourceService.postOne(
            this.apiResourceService.getApiUrl(
                AIR,
                BOOKINGS,
                syscode,
                CHECK,
            ),
            {
                booking: {
                    host: bookingHost,
                    code: bookingCode,
                }
            },
            {
                target,
            }
        );
    }


    public getOptimisation(syscode: string, airOfferId: string): Observable<any> {
        return this.apiResourceService.getOne<any>(
            this.apiResourceService.getApiUrl('booking', 'bookings', 'fareOptLog', 'optimisation', syscode, airOfferId)
        );
    }

    public issue(syscode: string, target: string): Observable<IAirBooking> {
        return this.apiResourceService.postOne(
            this.apiResourceService.getApiUrl(AIR, BOOKINGS, syscode, 'issue'),
            undefined,
            {
                target,
            }
        ).pipe(pluck(BOOKING));
    }
}
